import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Panel from './Panel';
import Avatar from './Avatar';
import { getToken } from '../utils/auth';
import './RequestsPanel.css';
import { carSizeLabel } from '../utils/carSize';
import { progressLine } from '../utils/handoverProgress';

// Every request that concerns you, in one place.
//
// 2026-10-15: there was no such place. Requests existed only per-spot, through SideDrawer and
// RequesterSideDrawer, which Map.js opens when a spot is selected — so to find out whether anybody
// had asked for your space you had to already know where your space was on the map and click it.
//
// 2026-10-16: made to match the phone's Requests tab, which had gone further. Three differences
// mattered, and the third is the one that made this a data problem rather than a layout one:
//
//   1. The sections are RECEIVED and SENT — someone waiting on your decision first, because they
//      may be circling the block, then what you are waiting on. (They were "On your space" and
//      "You asked for", which is the same idea in different words; two sets of words for one
//      concept is how two clients stop looking like one product.)
//   2. A received request is answerable HERE. Accept and Decline were on the spot drawer only, so
//      this panel could tell you somebody was waiting and then make you go and find them.
//   3. HISTORY existed in the database and nowhere on the web. GET /api/users/transactions has
//      returned completed handovers since 2026-09-20 and only the phone ever asked for them, so
//      the web could not show what happened, and — because a handover is rateable exactly once —
//      a web user who dismissed the post-handover prompt could never rate anybody again.
//
// Sent is derived from parkingSpots' my_request_status rather than from the pendingRequests id
// list, which is the phone's approach and strictly better: the id list is pending-only, so a
// request the owner had ACCEPTED silently vanished from this panel at the moment it started to
// matter most.
// One card shape for all three sections: identity, a sentence, a quiet second line, then any
// actions on their own row. The same anatomy as the phone's RequestCard.
//
// Module scope, deliberately — the same note as RequestsScreen.js. Declared inside the panel it
// would be a NEW component type on every render, so React would unmount and remount every row, and
// every avatar would be fetched again each time anything on the panel changed.
// inlineAction: a single short action sits on the avatar's row rather than claiming a row of its
// own — the phone's word and the phone's reason, and the half of its RequestCard this port left
// out. A history row is a sentence and one "Rate"; giving that its own row below made every row in
// the list half as dense again for no gain. Received keeps the row beneath, where two buttons
// would otherwise crowd the name out.
const Card = ({ name, avatarUrl, title, sub, subTone, onIdentity, children, inlineAction }) => (
  <div className="req-card">
    <div className="req-card-top">
      {/* The name and the picture both open the profile — the name is the part people read and
          reach for. A plain button, so it is reachable by keyboard as well. */}
      <button
        type="button"
        className="req-identity"
        onClick={onIdentity}
        disabled={!onIdentity}
        aria-label={onIdentity ? `See ${name || 'this driver'}'s profile` : undefined}
      >
        <Avatar username={name} avatarUrl={avatarUrl} size={38} />
      </button>
      <div className="req-card-text">
        <button
          type="button"
          className="req-title-button"
          onClick={onIdentity}
          disabled={!onIdentity}
        >
          {title}
        </button>
        {sub && <span className={`req-sub${subTone ? ` is-${subTone}` : ''}`}>{sub}</span>}
      </div>
      {inlineAction && children}
    </div>
    {!inlineAction && children && <div className="req-actions">{children}</div>}
  </div>
);

const RequestsPanel = ({
  open,
  onClose,
  spotRequests = [],
  parkingSpots = [],
  serverUrl = '',
  onSelectSpot,
  onOpenRequester,
  onDecline,
  onCancelRequest,
  onRate,
  onMessage,
  onRelease,
}) => {
  const [transactions, setTransactions] = useState([]);
  // 2026-10-29: "3 min ago" on an accepted requester's line ages on its own; re-render while one is.
  const [, setTick] = useState(0);
  const anyAccepted = spotRequests.some((r) => r.status === 'accepted');
  useEffect(() => {
    if (!open || !anyAccepted) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [open, anyAccepted]);

  const fetchTransactions = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${serverUrl}/api/users/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (res.ok) setTransactions(await res.json());
    } catch (e) {
      // Silent, as on the phone: history is the least urgent thing here, and a failed fetch should
      // leave the section absent rather than put an error in front of someone waiting on a request.
      console.warn('[Requests] transactions fetch failed:', e?.message);
    }
  }, [serverUrl]);

  // On every opening, so a handover that completed while the panel was shut is here when it opens.
  useEffect(() => { if (open) fetchTransactions(); }, [open, fetchTransactions]);

  // Spots you have asked for: pending, and accepted too — an accepted request is the one you most
  // need to see.
  const sent = useMemo(
    () => (parkingSpots || []).filter(
      (s) => s.my_request_status === 'pending' || s.my_request_status === 'accepted'
    ),
    [parkingSpots]
  );

  const total = spotRequests.length + sent.length;

  const when = (iso) => {
    if (!iso) return '';
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const h = Math.round(mins / 60);
    return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
  };

  const renderReceived = (r) => {
    const name = r.requester_username;
    // 2026-10-29: how far away they are (distance and ETA only — the server never sends where), a
    // way to talk to them, and once they are late, to stop waiting. Mirrors the phone's card.
    if (r.status === 'accepted') {
      // isLate from the live event; is_late from the list, for a panel opened after the nudge.
      const late = !!(r.isLate || r.is_late);
      return (
        <Card
          key={`received-${r.id}`}
          name={name}
          avatarUrl={r.requester_avatar_url}
          title={<><b>{name}</b> is coming</>}
          sub={progressLine(r.progress, { late, waitDeadline: r.wait_deadline ? new Date(r.wait_deadline).getTime() : null })}
          subTone={late ? 'noshow' : 'accepted'}
          onIdentity={() => onOpenRequester?.(r)}
        >
          {late && (
            <button
              type="button"
              className="btn btn--danger-quiet btn--sm"
              onClick={() => {
                if (window.confirm(`Release your spot? ${name || 'They'} will be marked as not having arrived.`)) onRelease?.(r);
              }}
            >
              Release spot
            </button>
          )}
          <button type="button" className="btn btn--primary btn--sm" onClick={() => onMessage?.(r)}>
            Message
          </button>
        </Card>
      );
    }
    return (
      <Card
        key={`received-${r.id}`}
        name={name}
        avatarUrl={r.requester_avatar_url}
        title={<><b>{name}</b> wants your spot</>}
        sub={[r.eta_minutes ? `about ${r.eta_minutes} min to get here` : null, carSizeLabel(r.requester_car_type), when(r.requested_at)].filter(Boolean).join(' · ')}
        onIdentity={() => onOpenRequester?.(r)}
      >
        {/* 2026-10-17: Decline only. Accepting is a promise somebody collects by driving to you,
            and it needs you at the space to hand it over — so it belongs on the phone. Declining
            releases an obligation instead of creating one, and doing it quickly frees the requester
            to look elsewhere, which is worth more than tidiness. */}
        <span className="req-hint">Accept from your phone</span>
        <button type="button" className="btn btn--danger-quiet btn--sm" onClick={() => onDecline?.(r)}>
          Decline
        </button>
      </Card>
    );
  };

  const renderSent = (spot) => {
    const name = spot.username || 'A driver';
    const accepted = spot.my_request_status === 'accepted';
    return (
      <Card
        key={`sent-${spot.id}`}
        name={spot.username}
        avatarUrl={spot.avatar_url}
        title={<><b>{name}</b>’s spot</>}
        sub={accepted ? 'Accepted — head to the spot' : 'Waiting for a reply'}
        subTone={accepted ? 'accepted' : undefined}
      >
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => onSelectSpot?.(spot)}>
          Show on map
        </button>
        {/* 2026-10-29: accepted too. The owner now holds the spot until THEY end it, so a requester
            who is not coming has to be able to say so rather than leave them waiting. */}
        {!accepted ? (
          <button type="button" className="btn btn--danger-quiet btn--sm" onClick={() => onCancelRequest?.(spot.id)}>
            Cancel
          </button>
        ) : (
          <button
            type="button"
            className="btn btn--danger-quiet btn--sm"
            onClick={() => {
              if (window.confirm(`Not coming? ${name} will be told straight away, and the spot goes back to other drivers.`)) onCancelRequest?.(spot.id);
            }}
          >
            Not coming
          </button>
        )}
      </Card>
    );
  };

  // A finished handover: what happened, when, and — once — the chance to rate them. Rated rows keep
  // their place and say so rather than disappearing: this is a record of what happened, not a
  // to-do list that empties itself.
  const renderHistory = (tx) => {
    const date = tx.happened_at
      ? new Date(tx.happened_at).toLocaleDateString([], { day: 'numeric', month: 'short' })
      : null;
    const other = tx.other_username;
    let title;
    if (tx.outcome === 'no_show') {
      title = tx.role === 'gave'
        ? <><b>{other}</b> never arrived</>
        : <>You never arrived for <b>{other}</b>’s spot</>;
    } else if (tx.outcome === 'unconfirmed') {
      title = <>Never confirmed with <b>{other}</b></>;
    } else if (tx.outcome === 'owner_cancelled') {
      title = tx.role === 'gave'
        ? <>You cancelled on <b>{other}</b></>
        : <><b>{other}</b> cancelled after accepting</>;
    } else {
      title = tx.role === 'gave'
        ? <>You gave your spot to <b>{other}</b></>
        : <>You took <b>{other}</b>’s spot</>;
    }
    const note = tx.outcome === 'unconfirmed' ? 'no one was rated' : (tx.rated ? 'rated' : null);
    return (
      <Card
        key={`history-${tx.id}`}
        name={other}
        avatarUrl={tx.other_avatar_url}
        title={title}
        sub={[date, note].filter(Boolean).join(' · ')}
        subTone={tx.outcome === 'no_show' || tx.outcome === 'owner_cancelled' ? 'noshow' : undefined}
        inlineAction
        onIdentity={() => onOpenRequester?.({
          // The profile card reads the owner's side of a request; a history row is the same person
          // under different column names.
          requester_id: tx.other_user_id,
          requester_username: tx.other_username,
          requester_avatar_url: tx.other_avatar_url,
        })}
      >
        {/* `rateable` is the SERVER's answer, not ours: a completed handover entitles both sides, a
            no-show only the owner who lost the time, an unconfirmed one nobody. */}
        {tx.rateable && !tx.rated && (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => onRate?.(tx, () => {
              setTransactions((prev) => prev.map((t) => (t.id === tx.id ? { ...t, rated: true } : t)));
              fetchTransactions();
            })}
          >
            Rate
          </button>
        )}
      </Card>
    );
  };

  return (
    <Panel open={open} title="Requests" count={total} onClose={onClose} labelledBy="requests-panel-title">
      {total === 0 && transactions.length === 0 && (
        <p className="panel-empty">
          No requests right now.
          <br />
          When somebody asks for your space, or you ask for theirs, it appears here.
        </p>
      )}

      {/* Received first, deliberately: somebody is waiting on your decision and may be circling the
          block, whereas a sent request needs nothing from you. Empty sections are omitted rather
          than shown with placeholder text — being only one of the two roles is the common case and
          should not read as half the panel being broken. */}
      {spotRequests.length > 0 && (
        <>
          <div className="panel-section-title">Received{spotRequests.length > 1 ? ` · ${spotRequests.length}` : ''}</div>
          {spotRequests.map(renderReceived)}
        </>
      )}

      {sent.length > 0 && (
        <>
          <div className="panel-section-title">Sent{sent.length > 1 ? ` · ${sent.length}` : ''}</div>
          {sent.map(renderSent)}
        </>
      )}

      {transactions.length > 0 && (
        <>
          <div className="panel-section-title">History</div>
          {transactions.map(renderHistory)}
        </>
      )}
    </Panel>
  );
};

export default RequestsPanel;
